/*
 * The master inbox list's state that is not presentation: the filters a rep sets, and the two
 * choices kept for them between visits (master-inbox plan, step 3: docs/2026-10-17-master-inbox-in-
 * the-existing-ui-plan.md, "List, workspaces and teamwork").
 *
 * - The order, Newest or Oldest, is kept per view: per workspace and per place in its inbox, so the
 *   Done box can be read oldest first while the Inbox stays newest first.
 * - The list's mode, Classic or Highlights, is one choice per person (Dawid, 2026-10-17: "feels lik
 *   classic inbox with optional top 3 separatey highlighted at the top? ... Id maybe offer 2
 *   options somehow"). Classic is the default.
 *
 * Both are stored the way the shell stores the theme and the pane widths: in this browser, under the
 * person's own id, so two people sharing a browser keep their own. Storage can be unavailable, which
 * is the default and never an error. Neither choice ever changes what the API returns; the rows a
 * list holds are the server's.
 */
import { useEffect, useState } from "react";
import { z } from "zod";
import type { AgentStatus } from "../../src/inbox-v2/agent-contract.ts";
import type { InboxLabelView } from "../../src/inbox-v2/contract.ts";
import { readPreference } from "../preferences.ts";

/** The list's own filters. Campaign and Files are search operators (`search-query.ts`). */
export type ListFilters = {
  /**
   * Meeting requests: the Interested replies asking to meet that need an answer (LEA-494). Needs
   * reply: the lead's latest human reply is unanswered, apart from meeting requests. Waiting on lead:
   * the seller wrote last. A saved view never keeps Meeting requests (`view-query.ts`).
   */
  status: "meeting" | "needs_reply" | "waiting" | null;
  /**
   * The Status filter's Reply agent choice (step 9), for a person with the switch on, beside
   * `status`, which narrows with it. Asked of its own route, never of the inbox's or kept in a
   * saved view.
   */
  agent: AgentStatus | null;
  unread: boolean;
  /** Only replies from the last so many days; null is all time. */
  days: number | null;
  /** Only labels Jev was unsure of that no rep has settled. */
  check: boolean;
  /**
   * One label, whatever the status: how Inbox, a box or a saved view reaches every interested
   * thread, now that a rail label row lists only those waiting on our side.
   */
  label: InboxLabelView | null;
};

export type ListSort = "newest" | "oldest";
export type ListMode = "classic" | "highlights";

const SortsSchema = z.record(z.string(), z.enum(["newest", "oldest"]));

const modeKey = (userId: string) => `leadcue-reply-inbox-mode:${userId}`;
const sortKey = (userId: string) => `leadcue-reply-inbox-sort:${userId}`;

function read(key: string): string | null {
  try {
    return readPreference(localStorage, key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the choice still holds for this visit.
  }
}

function readSorts(userId: string): Record<string, ListSort> {
  const raw = read(sortKey(userId));
  if (raw === null) return {};
  try {
    const parsed = SortsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}

/** The mode this person last chose here; Classic when none. */
export function storedMode(userId: string): ListMode {
  return read(modeKey(userId)) === "highlights" ? "highlights" : "classic";
}

/** The order this person last chose for `view` here, if any. */
export function storedSort(userId: string, view: string): ListSort | null {
  return readSorts(userId)[view] ?? null;
}

const personKey = "leadcue-reply-inbox-person";

/**
 * The person whose inbox this browser last drew, so their list can be asked before the session
 * answers (`first-reads.ts`); null when none was.
 */
export function rememberedPerson(): string | null {
  return read(personKey);
}

/** Keeps `userId` as the person this browser's inbox last drew. */
export function useRememberPerson(userId: string): void {
  useEffect(() => {
    if (read(personKey) !== userId) write(personKey, userId);
  }, [userId]);
}

/** The person's list mode, and a setter that keeps it for their next visit. */
export function useListMode(userId: string): [ListMode, (mode: ListMode) => void] {
  const [mode, setMode] = useState<ListMode>(() => storedMode(userId));
  return [
    mode,
    (next) => {
      setMode(next);
      write(modeKey(userId), next);
    },
  ];
}

/**
 * The order of one view: what the person chose for it last, else the view's own default (a saved
 * view's stored order, or Newest). `view` names the view, workspace included.
 */
export function useListSort(
  userId: string,
  view: string,
  fallback: ListSort,
): [ListSort, (sort: ListSort) => void] {
  const [chosen, setChosen] = useState<Record<string, ListSort>>(() => readSorts(userId));
  return [
    chosen[view] ?? fallback,
    (sort) => {
      const next = { ...readSorts(userId), [view]: sort };
      setChosen(next);
      write(sortKey(userId), JSON.stringify(next));
    },
  ];
}
